import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Event } from '../../models/Event'
import { eventSchema } from '../../models/schema'
import {
  createEvent as createEventAPI,
  deleteEvent as deleteEventAPI,
  fetchEventById,
  fetchEvents,
  updateEvent as updateEventAPI,
} from '../../services/EventService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<Event> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'event',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getEventsSuccess,
  setQuery: setEventQuery,
  reset: resetEventState,
  resetQueryAndIds: resetEventQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Event }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectEvents = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedEvent = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectEventById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectEventsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedEvent =
  (event: Event | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Event>(event)))

export const getEvents = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchEvents(params),
    [eventSchema],
    slice.actions
  )

export const createEvent = (event: FormData) =>
  SBAPI.CreateDispatched(createEventAPI(event), eventSchema, slice.actions)

export const getEventWithId = (id: number) =>
  SBAPI.FetchDispatched<Event>(fetchEventById(id), eventSchema, slice.actions)

export const updateEvent = (event: FormData) =>
  SBAPI.UpdateDispatched<Event>(updateEventAPI(event), event, slice.actions)

export const deleteEvent = (event: Event) =>
  SBAPI.DestroyDispatched<Event>(deleteEventAPI(event), event, slice.actions)
